import {
  SoftphoneProvider,
  callPeerNumber,
  useSoftphone
} from "@dialstack/sdk-react/softphone";
import type { SoftphoneProviderProps } from "@dialstack/sdk-react/softphone";
import {
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import { selectCurrentUser } from "features/auth/auth-slice";
import { useBrowserNotifications } from "features/browser-notifications/browser-notifications.context";
import { CALL_DECLINED_WHILE_RECORDING_MESSAGE } from "features/medical-scribe/medical-scribe-constants";
import usePracticeVoiceEnabled from "hooks/use-practice-voice-enabled.hook";
import useVoiceSeat from "hooks/use-voice-seat.hook";
import { dialstackApi } from "services/dialstack";
import type {
  DialDevice,
  OutboundCallerId,
  UserDevice
} from "services/dialstack";
import type { Patient } from "services/types/patients.type";

import { selectOutboundCallProgress } from "../outbound-call-progress.slice";
import { lastTenDigits } from "../phone-match.util";
import { softphoneAppearance } from "./dialstack-softphone-widget.styles";
import useCallWaitingRing from "./use-call-waiting-ring.hook";
import { useDialDevicePreference } from "./use-dial-device-preference.hook";
import { useDialstackSessionToken } from "./use-dialstack-session-token.hook";
import useGhostCallGuard from "./use-ghost-call-guard.hook";
import useInboundCallerNames from "./use-inbound-caller-names.hook";
import { useIncomingCallNotifications } from "./use-incoming-call-notifications.hook";
import { useScribeRecordingActive } from "./use-scribe-recording-active.hook";
import {
  useSoftphoneOnCallLock,
  useSoftphoneTabLeader
} from "./use-softphone-tab-leader.hook";

// The patient a click-to-call surface dialed on behalf of. The SDK's dial()
// takes only a number, so this context travels beside it and names the
// outbound call in our own UI.
export type OutboundCallPatient = {
  id: number;
  name: string;
  // The patient's preferred clinic: its number is the default caller ID when
  // dialing them (ENG-1938). Absent when the caller did not load the field.
  preferredClinicId?: number;
};

export function toOutboundCallPatient(
  patient?: Partial<Patient>
): OutboundCallPatient | undefined {
  if (!patient?.id) return undefined;
  const name =
    patient.fullName ||
    `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim();
  if (!name) return undefined;
  const preferredClinicId = patient.preferred_clinic?.id;
  return {
    id: Number(patient.id),
    name,
    ...(preferredClinicId
      ? { preferredClinicId: Number(preferredClinicId) }
      : {})
  };
}

export type DialOptions = {
  // The caller ID chosen in the widget for this dial; null = automatic (the
  // patient's clinic number when dialing a patient, else the standing one).
  // Click-to-call surfaces outside the widget never pass one.
  callerId?: string | null;
  // Which device this one dial is for; absent = the standing choice
  // (`dialDevice`). ⌥-click on a phone icon passes the other one (ENG-2183).
  device?: DialDevice;
};

export type DeskPhoneDialPhase = "waiting" | "answered" | "no_answer" | "ended";

// A click-to-call the user is taking on their desk phone (ENG-2183), from the
// request until its result card is dismissed. The SDK never sees this call —
// the platform rings the handset, and the browser learns how it went from
// the backend's webhook pushes (outbound-call-progress.slice).
export type DeskPhoneDial = {
  // Last ten digits of the destination, the key the progress frames match on.
  destination: string;
  dialString: string;
  patient?: OutboundCallPatient;
  // The number presented, when one was chosen; the first leg may show it.
  callerNumber: string | null;
  at: number;
  phase: DeskPhoneDialPhase;
  answeredAt: number | null;
  endedAt: number | null;
  callStatus: string | null;
};

// The caller ID a dial presents: an explicit pick, else the number of the
// patient's preferred clinic, else whatever the platform presents on its own.
export function resolveOutboundCallerId(
  callerIds: OutboundCallerId[],
  standingCallerId: string | null,
  patient?: OutboundCallPatient,
  override?: string | null
): string | null {
  if (override && callerIds.some((number) => number.id === override))
    return override;
  const clinicNumber = patient?.preferredClinicId
    ? callerIds.find((number) => number.clinic_id === patient.preferredClinicId)
    : undefined;
  return clinicNumber?.id ?? standingCallerId;
}

export type DialstackSoftphoneContextValue = {
  // True when the softphone provider is mounted (session minted, SDK active).
  enabled: boolean;
  isOpen: boolean;
  openDialer: () => void;
  closeDialer: () => void;
  // The call pop folded down to its bar (ENG-2226). Every new ring and every
  // placed call unfold it again: the information is the point of the pop,
  // folding is for the moment the person needs the screen back.
  popFolded: boolean;
  foldPop: () => void;
  unfoldPop: () => void;
  // Open the widget and place a call. No-op when the softphone is unavailable
  // (callers should check `enabled` to decide whether to fall back).
  dialNumber: (
    destination: string,
    patient?: OutboundCallPatient,
    options?: DialOptions
  ) => void;
  // The numbers this user may call from (ENG-1938). A "call from" choice only
  // makes sense with more than one; `standingCallerId` is the one the platform
  // presents when a call names none (null when that cannot be told).
  callerIds: OutboundCallerId[];
  standingCallerId: string | null;
  // The caller ID picked in the widget for the next dial; cleared once a call
  // is placed, so the choice is per call. null = automatic.
  selectedCallerId: string | null;
  setSelectedCallerId: (id: string | null) => void;
  // A dial from a non-standing number is placed by the platform, which rings
  // this softphone first; true from the request until that leg is answered.
  providerDialPending: boolean;
  // The user's hardware phones (ENG-2183). Empty = the computer is the only
  // device, and nothing below applies.
  devices: UserDevice[];
  // Where a dial goes unless a call says otherwise: the per-browser
  // preference, downgraded to the computer while no desk phone is online.
  dialDevice: DialDevice;
  setDialDevice: (device: DialDevice) => void;
  // Read the user's devices again now (ENG-2539): the dialer calls this as it
  // opens and the "Call using" menu as it opens, so a phone set up since the
  // page loaded is there when the user goes looking for it.
  refreshDevices: () => void;
  // The desk-phone call in progress (or its lingering result), if any.
  deskPhoneDial: DeskPhoneDial | null;
  // The platform's leg of that call while it rings here. It is the user's own
  // call, not someone calling in: the widget raises no pop or caller card for
  // it, and the desk-phone card's "Use computer instead" is how it is taken.
  deskPhoneLegId: string | null;
  dismissDeskPhoneDial: () => void;
  // Take the current desk-phone dial on this computer instead: answer the
  // platform's leg if it is ringing here, else place the call outright.
  moveDeskPhoneDialToComputer: () => void;
  // Patient context for outbound calls placed via dialNumber, keyed by the
  // destination's last ten digits.
  outboundCallPatients: Record<string, OutboundCallPatient>;
  // The softphone presents its own ringing UI, so the generic websocket
  // screen-pop snackbar is redundant while it is connected.
  suppressIncomingSnackbar: boolean;
  // A usable phone is expected but not here yet: the session is being minted
  // (first load, or again after the phone died — ENG-1966). The widget shows
  // a connecting chip in place of the FAB instead of nothing.
  connecting: boolean;
  // Another tab holds the softphone (tab-leader election, APP-61): this tab
  // must not connect and offers a take-over affordance instead.
  blockedByOtherTab: boolean;
  // The practice has Voice but this user has no seat (ENG-2315): no session is
  // minted and nothing can dial. The FAB and every call icon stay, and
  // opening either (`isOpen`) shows how to get Voice turned on instead.
  needsVoiceSeat: boolean;
  // Steal softphone leadership from the tab that holds it: the other tab
  // disconnects and this one connects. Resolves false, and leaves the phone
  // where it is, while the other tab is on a call — moving would hang up on
  // the caller (ENG-2178).
  takeOverPhone: () => Promise<boolean>;
  // Permanently remove a saved E911 address. Lives here because this is where
  // the provider credential is held — see the implementation for why it is a
  // raw REST call rather than the SDK's own method.
  deleteEmergencyAddress: (id: string) => Promise<void>;
  // A Scribe recording is live in this browser, in this tab or another
  // (ENG-2001). Inbound calls are declined while it is; the status dot says so.
  scribeRecordingActive: boolean;
};

const INERT_VALUE: DialstackSoftphoneContextValue = {
  enabled: false,
  isOpen: false,
  openDialer: () => undefined,
  closeDialer: () => undefined,
  popFolded: false,
  foldPop: () => undefined,
  unfoldPop: () => undefined,
  dialNumber: () => undefined,
  callerIds: [],
  standingCallerId: null,
  selectedCallerId: null,
  setSelectedCallerId: () => undefined,
  providerDialPending: false,
  devices: [],
  dialDevice: "softphone",
  setDialDevice: () => undefined,
  refreshDevices: () => undefined,
  deskPhoneDial: null,
  deskPhoneLegId: null,
  dismissDeskPhoneDial: () => undefined,
  moveDeskPhoneDialToComputer: () => undefined,
  outboundCallPatients: {},
  suppressIncomingSnackbar: false,
  connecting: false,
  blockedByOtherTab: false,
  needsVoiceSeat: false,
  takeOverPhone: async () => false,
  deleteEmergencyAddress: () =>
    Promise.reject(new Error("Softphone is unavailable")),
  scribeRecordingActive: false
};

const CONNECTING_VALUE: DialstackSoftphoneContextValue = {
  ...INERT_VALUE,
  connecting: true
};

const DialstackSoftphoneContext =
  createContext<DialstackSoftphoneContextValue>(INERT_VALUE);

export const useDialstackSoftphone = () =>
  useContext(DialstackSoftphoneContext);

// True while the widget draws something in the viewport's bottom-right corner
// (the FAB, the connecting chip, the standby FAB, or the no-seat FAB).
// Composers that pin Send there pad by `SOFTPHONE_FAB_CLEARANCE_PX` so the
// phone never covers it (SN-19774).
export const isSoftphoneFabInCorner = ({
  enabled,
  connecting,
  blockedByOtherTab,
  needsVoiceSeat
}: Pick<
  DialstackSoftphoneContextValue,
  "enabled" | "connecting" | "blockedByOtherTab" | "needsVoiceSeat"
>) => enabled || connecting || blockedByOtherTab || needsVoiceSeat;

export const useSoftphoneFabInCorner = () =>
  isSoftphoneFabInCorner(useDialstackSoftphone());

// How long after a clean (non-'failed') call end a surfacing `call_failed`
// error is still attributed to that teardown rather than to a new attempt.
const STALE_CALL_FAILED_WINDOW_MS = 2500;

// The SDK's own default, and no `apiBaseUrl` is passed to `SoftphoneProvider`
// below — so this is the host our session token is already valid against. If a
// future change sets `apiBaseUrl`, this must move with it.
const DIALSTACK_API_BASE_URL = "https://api.dialstack.ai";

// Repeat click-to-calls inside this window are treated as the same intent.
// The in-flight check below covers the call once the SDK lists it; this
// cooldown covers the dial → call-appears-in-state latency where a rapid
// double-click would otherwise place two real calls.
export const REDIAL_COOLDOWN_MS = 3000;

// How long a just-dialed outbound patient context survives without a matching
// call in the SDK state — covers the dial → call-appears latency. Once the
// call has come and gone, the entry is pruned so a later hand-dial of the
// same number isn't labeled with a stale patient.
const OUTBOUND_PATIENT_GRACE_MS = 15_000;

// How long the "call declined while recording" snackbar stays up. Quiet on
// purpose: the clinician is mid-encounter and must not be pulled out of it.
const CALL_DECLINED_SNACKBAR_MS = 6000;

// How long after a click-to-call from a chosen caller ID the platform's first
// leg (it rings this softphone, then dials the destination on answer) is still
// expected. The platform queues the request asynchronously, so this is
// generous; a genuine inbound call after the window rings as normal.
const PROVIDER_DIAL_ANSWER_WINDOW_MS = 45_000;

const PROVIDER_DIAL_FAILED_SNACKBAR_MS = 8000;
const PROVIDER_DIAL_FAILED_MESSAGE =
  "The call could not be placed from that number. Try again, or call from your default number.";
export const DESK_PHONE_DIAL_FAILED_MESSAGE =
  "Your desk phone could not be rung. Try again, or call from this computer.";

// How long a desk-phone dial waits for a pickup before the widget offers the
// computer instead (ENG-2183). Nothing can cancel the ring itself — the
// platform has no API for that — so this only decides what the card says.
export const DESK_PHONE_ANSWER_WINDOW_MS = 45_000;
// How long a finished desk-phone dial's card stays: long enough to read, or
// to take "Call from computer" after a no-answer.
export const DESK_PHONE_RESULT_LINGER_MS = 20_000;

type ProviderDialRequest = {
  // Last ten digits of the destination and of the number being presented:
  // the first leg shows one or the other as its caller.
  destination: string;
  callerNumber: string;
  at: number;
};

type DeskPhoneDialRequest = Pick<
  DeskPhoneDial,
  "destination" | "dialString" | "patient" | "callerNumber" | "at"
>;

// The platform's first leg for a click-to-call rings THIS softphone too. It
// is recognised by who it appears to be from: the destination, the number
// being presented, or any of the practice's own numbers.
function isClickToCallFirstLeg(
  call: { direction: string; state: string },
  peer: string,
  request: { destination: string; callerNumber: string | null; at: number },
  ownNumbers: Set<string>,
  windowMs: number
) {
  if (Date.now() - request.at > windowMs) return false;
  if (!isInboundRinging(call)) return false;
  return (
    peer === request.destination ||
    peer === request.callerNumber ||
    ownNumbers.has(peer)
  );
}

type OutboundPatientEntry = { patient: OutboundCallPatient; at: number };

type BridgeProps = {
  userId: number | null;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  popFolded: boolean;
  setPopFolded: (folded: boolean) => void;
  connecting: boolean;
  scribeRecordingActive: boolean;
  lastCleanCallEndAtRef: MutableRefObject<number | null>;
  deleteEmergencyAddress: (id: string) => Promise<void>;
  children: ReactNode;
};

const isInboundRinging = (call: { direction: string; state: string }) =>
  call.direction === "inbound" && call.state === "ringing";

// Rendered under SoftphoneProvider so it can reach the SDK context and expose
// our app-level API (open/close/dial) to everything inside the layout.
function DialstackSoftphoneBridge({
  userId,
  isOpen,
  setIsOpen,
  popFolded,
  setPopFolded,
  connecting,
  scribeRecordingActive,
  lastCleanCallEndAtRef,
  deleteEmergencyAddress,
  children
}: BridgeProps) {
  const {
    connection,
    placeCall,
    answerCall,
    calls,
    activeCall,
    lastError,
    clearError
  } = useSoftphone();

  // Tells standby tabs this one is on a call, so their take-over refuses
  // instead of hanging up on the caller (ENG-2178).
  useSoftphoneOnCallLock(calls.length > 0);

  // A practice's numbers change through Voice Setup, not mid-shift, but its
  // phones do: a handset is paired and assigned while the front desk's tab
  // stays open all day, and its registration comes and goes (ENG-2539). The
  // provider has no event for an assignment, so the list is read again each
  // time the dialer opens and each time the device menu opens — the moments
  // someone is looking for their phone. Until the first read arrives (or when
  // it fails) there is no choice to offer and every dial goes straight through
  // the SDK as before.
  const { data: callerIdData, refetch: refetchCallerIds } =
    dialstackApi.endpoints.getOutboundCallerIds.useQuery();
  const refreshDevices = useCallback(() => {
    refetchCallerIds();
  }, [refetchCallerIds]);
  const callerIds = useMemo(() => callerIdData?.numbers ?? [], [callerIdData]);
  const standingCallerId = callerIdData?.standing_id ?? null;
  const [selectedCallerId, setSelectedCallerId] = useState<string | null>(null);
  // `?? []` also covers a backend that predates the field.
  const devices = useMemo(() => callerIdData?.devices ?? [], [callerIdData]);

  // Where dials go (ENG-2183): the per-browser preference, but only while a
  // desk phone is actually reachable — a dial to an unplugged handset would
  // ring nothing and the user would be left waiting.
  const [dialDevicePreference, setDialDevice] = useDialDevicePreference(userId);
  const deskPhoneOnline = devices.some((device) => device.online);
  const dialDevice: DialDevice =
    dialDevicePreference === "deskphone" && deskPhoneOnline
      ? "deskphone"
      : "softphone";

  // A click-to-call the platform is placing for us (chosen caller ID): its
  // first leg arrives here as an inbound call, which is answered on sight
  // below so the flow still feels like a direct dial.
  const [providerDial, setProviderDial] = useState<ProviderDialRequest | null>(
    null
  );
  // A click-to-call the user will pick up on the desk phone (ENG-2183): its
  // first leg rings here too, but must NOT be answered — and must not be
  // declined either, which could cancel the whole call on the platform.
  const [deskPhoneRequest, setDeskPhoneRequest] =
    useState<DeskPhoneDialRequest | null>(null);
  const ownNumbers = useMemo(
    () =>
      new Set(callerIds.map((number) => lastTenDigits(number.phone_number))),
    [callerIds]
  );
  const providerFirstLeg = useMemo(() => {
    if (!providerDial) return null;
    return (
      calls.find((call) =>
        isClickToCallFirstLeg(
          call,
          lastTenDigits(callPeerNumber(call)),
          providerDial,
          ownNumbers,
          PROVIDER_DIAL_ANSWER_WINDOW_MS
        )
      ) ?? null
    );
  }, [calls, ownNumbers, providerDial]);
  const deskPhoneFirstLeg = useMemo(() => {
    if (!deskPhoneRequest) return null;
    return (
      calls.find((call) =>
        isClickToCallFirstLeg(
          call,
          lastTenDigits(callPeerNumber(call)),
          deskPhoneRequest,
          ownNumbers,
          DESK_PHONE_ANSWER_WINDOW_MS
        )
      ) ?? null
    );
  }, [calls, deskPhoneRequest, ownNumbers]);
  // Answered legs are remembered until they leave the SDK's list: the SDK
  // may still report one as ringing for a render or two after `answerCall`.
  const answeredProviderLegIdsRef = useRef(new Set<string>());
  useEffect(() => {
    if (!providerFirstLeg) return;
    answeredProviderLegIdsRef.current.add(providerFirstLeg.id);
    setProviderDial(null);
    answerCall(providerFirstLeg);
  }, [answerCall, providerFirstLeg]);
  useEffect(() => {
    const answered = answeredProviderLegIdsRef.current;
    const liveIds = new Set(calls.map((call) => call.id));
    answered.forEach((id) => {
      if (!liveIds.has(id)) answered.delete(id);
    });
  }, [calls]);
  const deskPhoneLegId = deskPhoneFirstLeg?.id ?? null;
  const isOwnProviderLeg = useCallback(
    (call: { id: string }) =>
      call === providerFirstLeg ||
      call === deskPhoneFirstLeg ||
      answeredProviderLegIdsRef.current.has(call.id),
    [deskPhoneFirstLeg, providerFirstLeg]
  );

  // How the desk-phone call is going, from the backend's webhook pushes.
  // Frames before the request are an earlier call to the same number.
  const deskPhoneProgress = useAppSelector((state) =>
    deskPhoneRequest
      ? selectOutboundCallProgress(
          state,
          deskPhoneRequest.destination,
          deskPhoneRequest.at
        )
      : undefined
  );
  const [deskPhoneTimedOut, setDeskPhoneTimedOut] = useState(false);
  useEffect(() => {
    setDeskPhoneTimedOut(false);
    if (!deskPhoneRequest) return undefined;
    const timer = window.setTimeout(
      () => setDeskPhoneTimedOut(true),
      DESK_PHONE_ANSWER_WINDOW_MS
    );
    return () => window.clearTimeout(timer);
  }, [deskPhoneRequest]);
  const deskPhoneDial = useMemo<DeskPhoneDial | null>(() => {
    if (!deskPhoneRequest) return null;
    const answeredAt = deskPhoneProgress?.answeredAt
      ? Date.parse(deskPhoneProgress.answeredAt)
      : null;
    const endedAt = deskPhoneProgress?.endedAt
      ? Date.parse(deskPhoneProgress.endedAt)
      : null;
    let phase: DeskPhoneDialPhase = "waiting";
    if (deskPhoneProgress?.event === "ended")
      phase = answeredAt ? "ended" : "no_answer";
    else if (deskPhoneProgress?.event === "answered") phase = "answered";
    else if (deskPhoneTimedOut) phase = "no_answer";
    return {
      ...deskPhoneRequest,
      phase,
      answeredAt,
      endedAt,
      callStatus: deskPhoneProgress?.callStatus ?? null
    };
  }, [deskPhoneProgress, deskPhoneRequest, deskPhoneTimedOut]);
  // A finished dial's card leaves on its own; the user can also dismiss it.
  const deskPhoneDialDone =
    deskPhoneDial?.phase === "ended" || deskPhoneDial?.phase === "no_answer";
  useEffect(() => {
    if (!deskPhoneDialDone) return undefined;
    const timer = window.setTimeout(
      () => setDeskPhoneRequest(null),
      DESK_PHONE_RESULT_LINGER_MS
    );
    return () => window.clearTimeout(timer);
  }, [deskPhoneDialDone]);
  const dismissDeskPhoneDial = useCallback(() => setDeskPhoneRequest(null), []);
  // Forget a request the platform never rang for (it refused the call after
  // accepting it, or the user answered on a desk phone instead).
  useEffect(() => {
    if (!providerDial) return undefined;
    const remaining =
      providerDial.at + PROVIDER_DIAL_ANSWER_WINDOW_MS - Date.now();
    const timer = window.setTimeout(
      () => setProviderDial(null),
      Math.max(remaining, 0)
    );
    return () => window.clearTimeout(timer);
  }, [providerDial]);

  // When the REMOTE side disconnects, the platform can emit a spurious
  // `call_failed` transport error during teardown; the SDK renders it as a
  // persistent "Call failed" chip that survives the call ending cleanly.
  // Dismiss a call_failed that surfaces around a clean end. Genuine failures
  // keep the chip: a 'failed' end never records a clean end, errors during a
  // live call are left alone, and anything outside the teardown window (e.g.
  // a later dial that legitimately fails) stays visible.
  useEffect(() => {
    if (!lastError || lastError.code !== "call_failed") return;
    if (activeCall || calls.length > 0) return;
    const cleanEndAt = lastCleanCallEndAtRef.current;
    if (cleanEndAt === null) return;
    if (Date.now() - cleanEndAt > STALE_CALL_FAILED_WINDOW_MS) return;
    clearError();
  }, [activeCall, calls, clearError, lastCleanCallEndAtRef, lastError]);

  const dispatch = useAppDispatch();

  // A ringing inbound call makes the SDK open the microphone before anyone
  // answers, which takes it away from a live Scribe recording on a Bluetooth
  // headset (ENG-2001). While a recording is live anywhere in this browser,
  // decline inbound calls as busy the moment they ring so the SDK never
  // reaches for the microphone. Declined ids are remembered until the call
  // leaves the SDK's list so a re-render cannot reject the same call twice.
  const declinedCallIdsRef = useRef(new Set<string>());
  useEffect(() => {
    const declined = declinedCallIdsRef.current;
    const liveIds = new Set(calls.map((call) => call.id));
    declined.forEach((id) => {
      if (!liveIds.has(id)) declined.delete(id);
    });
    if (!scribeRecordingActive) return;
    calls.forEach((call) => {
      if (!isInboundRinging(call) || declined.has(call.id)) return;
      // Our own click-to-call ringing back is not an interruption.
      if (isOwnProviderLeg(call)) return;
      declined.add(call.id);
      try {
        call.reject("busy");
      } catch (error) {
        // The call may already be gone (answered elsewhere, hung up) by the
        // time this runs; one bad reject must not skip the other ringing calls.
        console.warn("[softphone] could not decline call while recording", {
          callId: call.id,
          error
        });
      }
      dispatch(
        setCustomMessage(
          CALL_DECLINED_WHILE_RECORDING_MESSAGE(
            call.fromName || call.from || "Unknown caller"
          )
        )
      );
      dispatch(setSnackbarOpen(CALL_DECLINED_SNACKBAR_MS));
    });
  }, [calls, dispatch, isOwnProviderLeg, scribeRecordingActive]);

  const { offerPermission } = useBrowserNotifications();
  const openDialer = useCallback(() => {
    setIsOpen(true);
    offerPermission();
    // Opening the dialer is "I want to call now": a phone set up since the
    // page loaded must be here for that call, not for the next poll — and a
    // user whose first phone it is has no device row to open until it is.
    refreshDevices();
  }, [setIsOpen, offerPermission, refreshDevices]);
  const closeDialer = useCallback(() => setIsOpen(false), [setIsOpen]);
  const foldPop = useCallback(() => setPopFolded(true), [setPopFolded]);
  const unfoldPop = useCallback(() => setPopFolded(false), [setPopFolded]);
  // A call declined for a recording must not raise a browser notification
  // either — the clinician cannot answer it.
  const notifiableCalls = useMemo(
    () =>
      calls.filter(
        (call) =>
          !isOwnProviderLeg(call) &&
          !(scribeRecordingActive && isInboundRinging(call))
      ),
    [calls, isOwnProviderLeg, scribeRecordingActive]
  );
  const callerNames = useInboundCallerNames();
  useIncomingCallNotifications(
    notifiableCalls,
    openDialer,
    answerCall,
    callerNames
  );
  useCallWaitingRing();
  useGhostCallGuard();

  // Click-to-call must be idempotent: a second click for a number that is
  // already trying/ringing/connected places a REAL second call (the SDK is
  // multi-call — it auto-holds the first, and the duplicate typically hits
  // the callee's busy line and diverts to voicemail). Repeat requests just
  // surface the widget. Dialing a *different* number stays allowed — that
  // is deliberate multi-call use.
  const lastDialRef = useRef<{ destination: string; at: number } | null>(null);

  const [outboundPatientEntries, setOutboundPatientEntries] = useState<
    Record<string, OutboundPatientEntry>
  >({});

  // Prune entries whose call is gone (and grace elapsed). Runs on every SDK
  // call-state change, which includes the transition that removes the call.
  useEffect(() => {
    setOutboundPatientEntries((current) => {
      const entries = Object.entries(current);
      if (entries.length === 0) return current;
      const now = Date.now();
      const kept = entries.filter(
        ([destination, entry]) =>
          now - entry.at < OUTBOUND_PATIENT_GRACE_MS ||
          calls.some(
            (call) => lastTenDigits(callPeerNumber(call)) === destination
          )
      );
      return kept.length === entries.length
        ? current
        : Object.fromEntries(kept);
    });
  }, [calls]);

  const dialNumber = useCallback(
    (
      destination: string,
      patient?: OutboundCallPatient,
      options?: DialOptions
    ) => {
      setIsOpen(true);
      setPopFolded(false);
      offerPermission();
      const requested = lastTenDigits(destination);
      // Recorded even when the dial below is suppressed — the suppressed
      // cases mean a call to this number is already live, and labeling it
      // is exactly the point.
      if (requested && patient) {
        setOutboundPatientEntries((current) => ({
          ...current,
          [requested]: { patient, at: Date.now() }
        }));
      }
      if (requested) {
        const now = Date.now();
        const recentlyDialed =
          lastDialRef.current !== null &&
          lastDialRef.current.destination === requested &&
          now - lastDialRef.current.at < REDIAL_COOLDOWN_MS;
        // A desk-phone call never enters the SDK's list; its own state
        // stands in while it is waiting or up. A finished one (no answer,
        // ended) may be redialed straight away.
        const alreadyInCall =
          calls.some(
            (call) => lastTenDigits(callPeerNumber(call)) === requested
          ) ||
          (deskPhoneDial !== null &&
            !deskPhoneDialDone &&
            deskPhoneDial.destination === requested);
        if (recentlyDialed || alreadyInCall) return;
        lastDialRef.current = { destination: requested, at: now };
      }
      const trimmed = destination.trim();
      if (!trimmed) return;
      // The choice is per call: whatever was picked applies to this dial only.
      setSelectedCallerId(null);
      const callerId = resolveOutboundCallerId(
        callerIds,
        standingCallerId,
        patient,
        options?.callerId
      );
      const callerNumber = callerIds.find((number) => number.id === callerId);
      const device = options?.device ?? dialDevice;
      if (device === "deskphone" && devices.length > 0) {
        // The desk phone can only be reached through the platform's
        // click-to-call: it rings every device the user has (this softphone
        // included — left ringing, see above) and dials the destination once
        // one picks up. The backend writes the click row and pushes the
        // call's progress back over the websocket.
        setDeskPhoneRequest({
          destination: requested,
          dialString: trimmed,
          patient,
          callerNumber: callerNumber
            ? lastTenDigits(callerNumber.phone_number)
            : null,
          at: Date.now()
        });
        dispatch(
          dialstackApi.endpoints.initiateOutboundCall.initiate(
            {
              dial_string: trimmed,
              device: "deskphone",
              ...(callerId && callerNumber && callerId !== standingCallerId
                ? { did: callerId }
                : {}),
              ...(patient ? { patient_id: patient.id } : {})
            },
            { track: false }
          )
        )
          .unwrap()
          .catch((error: { data?: { detail?: unknown } }) => {
            setDeskPhoneRequest(null);
            const detail = error?.data?.detail;
            dispatch(
              setCustomMessage(
                typeof detail === "string" && detail
                  ? detail
                  : DESK_PHONE_DIAL_FAILED_MESSAGE
              )
            );
            dispatch(setSnackbarOpen(PROVIDER_DIAL_FAILED_SNACKBAR_MS));
          });
        return;
      }
      if (callerId && callerNumber && callerId !== standingCallerId) {
        // The SDK's own leg cannot carry a caller ID, so a number other than
        // the standing one goes through the platform's click-to-call: it
        // rings this softphone first (answered on arrival, above) and dials
        // the destination on answer. The backend writes the click row itself.
        setProviderDial({
          destination: requested,
          callerNumber: lastTenDigits(callerNumber.phone_number),
          at: Date.now()
        });
        dispatch(
          dialstackApi.endpoints.initiateOutboundCall.initiate(
            {
              dial_string: trimmed,
              did: callerId,
              ...(patient ? { patient_id: patient.id } : {})
            },
            { track: false }
          )
        )
          .unwrap()
          .catch((error: { data?: { detail?: unknown } }) => {
            setProviderDial(null);
            const detail = error?.data?.detail;
            dispatch(
              setCustomMessage(
                typeof detail === "string" && detail
                  ? detail
                  : PROVIDER_DIAL_FAILED_MESSAGE
              )
            );
            dispatch(setSnackbarOpen(PROVIDER_DIAL_FAILED_SNACKBAR_MS));
          });
        return;
      }
      if (patient) {
        // Tell the backend who this dial is calling so the Call record (the
        // /calls page, patient tab) attributes the patient. Best-effort: the
        // dial must never depend on it. Only on a real dial — a suppressed
        // repeat would orphan a second uncorrelated row.
        dispatch(
          dialstackApi.endpoints.registerOutboundCallContext.initiate(
            { dial_string: destination, patient_id: patient.id },
            { track: false }
          )
        );
      }
      // SDK 2.0 dropped the fire-and-forget `dial`, which was just `placeCall`
      // behind a trim-and-skip-empty guard — so do that here. The rejection is
      // dropped deliberately: failures surface through the provider's
      // `onError` → `lastError` chip that the effect above owns.
      placeCall(trimmed).catch(() => undefined);
    },
    [
      callerIds,
      calls,
      deskPhoneDial,
      deskPhoneDialDone,
      devices.length,
      dialDevice,
      dispatch,
      placeCall,
      setIsOpen,
      setPopFolded,
      offerPermission,
      standingCallerId
    ]
  );

  // "Use computer instead" on a desk-phone dial: the platform's own leg is
  // the quickest way in while it still rings here (answering it makes the
  // platform dial the destination from this softphone); otherwise place the
  // call afresh, as a plain dial would.
  const moveDeskPhoneDialToComputer = useCallback(() => {
    if (!deskPhoneRequest) return;
    setDeskPhoneRequest(null);
    if (deskPhoneFirstLeg) {
      answeredProviderLegIdsRef.current.add(deskPhoneFirstLeg.id);
      answerCall(deskPhoneFirstLeg);
      return;
    }
    // The request stays in `lastDialRef`, so bypass the redial cooldown and
    // the "already in call" guard by dialing directly.
    lastDialRef.current = null;
    if (deskPhoneRequest.patient) {
      dispatch(
        dialstackApi.endpoints.registerOutboundCallContext.initiate(
          {
            dial_string: deskPhoneRequest.dialString,
            patient_id: deskPhoneRequest.patient.id
          },
          { track: false }
        )
      );
    }
    placeCall(deskPhoneRequest.dialString).catch(() => undefined);
  }, [answerCall, deskPhoneFirstLeg, deskPhoneRequest, dispatch, placeCall]);

  const outboundCallPatients = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(outboundPatientEntries).map(([destination, entry]) => [
          destination,
          entry.patient
        ])
      ),
    [outboundPatientEntries]
  );

  const value = useMemo<DialstackSoftphoneContextValue>(
    () => ({
      enabled: true,
      isOpen,
      openDialer,
      closeDialer,
      popFolded,
      foldPop,
      unfoldPop,
      dialNumber,
      callerIds,
      standingCallerId,
      selectedCallerId,
      setSelectedCallerId,
      providerDialPending: providerDial !== null,
      devices,
      dialDevice,
      setDialDevice,
      refreshDevices,
      deskPhoneDial,
      deskPhoneLegId,
      dismissDeskPhoneDial,
      moveDeskPhoneDialToComputer,
      outboundCallPatients,
      suppressIncomingSnackbar: connection === "connected",
      connecting,
      blockedByOtherTab: false,
      takeOverPhone: async () => true,
      deleteEmergencyAddress,
      scribeRecordingActive
    }),
    [
      callerIds,
      closeDialer,
      connecting,
      connection,
      deleteEmergencyAddress,
      deskPhoneDial,
      deskPhoneLegId,
      devices,
      dialDevice,
      dialNumber,
      dismissDeskPhoneDial,
      foldPop,
      isOpen,
      moveDeskPhoneDialToComputer,
      openDialer,
      outboundCallPatients,
      popFolded,
      providerDial,
      refreshDevices,
      scribeRecordingActive,
      selectedCallerId,
      setDialDevice,
      standingCallerId,
      unfoldPop
    ]
  );

  return (
    <DialstackSoftphoneContext.Provider value={value}>
      {children}
    </DialstackSoftphoneContext.Provider>
  );
}

type DialstackSoftphoneProviderProps = {
  children: ReactNode;
};

export function DialstackSoftphoneProvider({
  children
}: DialstackSoftphoneProviderProps) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const isPracticeVoiceEnabled = usePracticeVoiceEnabled();
  // Only a seat holder gets a phone (ENG-2315). The mint below 409s
  // server-side for anyone else; gating here skips the doomed request, so a
  // browser without a seat never asks DialStack for a session.
  const hasVoiceSeat = useVoiceSeat();
  const userId =
    currentUser?.id && currentUser.id !== -1 ? currentUser.id : null;

  // One softphone per browser: only the leader tab may mint a session and
  // connect (APP-61 — a second connected session swallows inbound ringing).
  const wantsSoftphone = Boolean(hasVoiceSeat && userId);
  // Voice is on for the practice but not for this user: no phone and no tab
  // election, just the FAB that explains how to get a seat.
  const needsVoiceSeat = Boolean(
    isPracticeVoiceEnabled && userId && !hasVoiceSeat
  );
  const { isLeader, blockedByOtherTab, takeOver } =
    useSoftphoneTabLeader(wantsSoftphone);

  const [isOpen, setIsOpen] = useState(false);
  const [popFolded, setPopFolded] = useState(false);

  const mintToken = useCallback(async () => {
    const session = await dispatch(
      dialstackApi.endpoints.createDialstackUserSession.initiate(undefined, {
        track: false
      })
    ).unwrap();
    return session.client_secret;
  }, [dispatch]);

  const {
    token,
    sessionPending,
    refreshToken,
    handleError,
    handleConnectionStateChange
  } = useDialstackSessionToken({
    enabled: wantsSoftphone && isLeader,
    userId,
    mint: mintToken
  });

  // The SDK exposes `deleteEmergencyAddress` on `DialStackPhone`, but
  // `SoftphoneContextBase` publishes no `phone`, so it cannot be reached from
  // React. This is byte-for-byte the request its own method makes. The one
  // behaviour we lose is that it also clears the id it has cached in
  // localStorage; that self-heals, because the next connect falls back to
  // `addresses.find(presentedId) ?? addresses[0]` and rebinds.
  //
  // Minting a fresh secret rather than reusing `token`: that state only
  // changes when the phone is (re)created, while the SDK refreshes its own
  // copy through `onTokenExpiring` — so `token` is stale in any tab that has
  // been open past one expiry. The mint is also the authority on whether this
  // user has voice at all, so it doubles as the access check.
  const deleteEmergencyAddress = useCallback(
    async (id: string) => {
      const clientSecret = await mintToken();
      const response = await fetch(
        `${DIALSTACK_API_BASE_URL}/v1/me/emergency-addresses/${encodeURIComponent(
          id
        )}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${clientSecret}` }
        }
      );
      if (!response.ok)
        throw new Error(`Could not remove the address (${response.status})`);
    },
    [mintToken]
  );

  // Read through a ref so the SDK callback stays stable across recording
  // state changes; the bridge below declines the call itself (ENG-2001).
  const scribeRecordingActive = useScribeRecordingActive();
  const scribeRecordingActiveRef = useRef(scribeRecordingActive);
  useEffect(() => {
    scribeRecordingActiveRef.current = scribeRecordingActive;
  }, [scribeRecordingActive]);
  const handleIncomingCall = useCallback(() => {
    if (scribeRecordingActiveRef.current) return;
    setIsOpen(true);
    setPopFolded(false);
  }, []);
  const handleCallStarted = useCallback(() => {
    setIsOpen(true);
    setPopFolded(false);
  }, []);

  const lastCleanCallEndAtRef = useRef<number | null>(null);
  const handleCallEnded = useCallback<
    NonNullable<SoftphoneProviderProps["onCallEnded"]>
  >(({ reason }) => {
    if (reason !== "failed") lastCleanCallEndAtRef.current = Date.now();
  }, []);

  const standbyValue = useMemo<DialstackSoftphoneContextValue>(
    () => ({
      ...INERT_VALUE,
      blockedByOtherTab: true,
      takeOverPhone: takeOver
    }),
    [takeOver]
  );

  // Every way into the phone (the FAB, a click-to-call, the quick launcher)
  // opens the no-seat notice instead; a dialled number goes nowhere.
  const needsVoiceSeatValue = useMemo<DialstackSoftphoneContextValue>(
    () => ({
      ...INERT_VALUE,
      needsVoiceSeat: true,
      isOpen,
      openDialer: () => setIsOpen(true),
      closeDialer: () => setIsOpen(false),
      dialNumber: () => setIsOpen(true)
    }),
    [isOpen]
  );

  if (!token) {
    const value = needsVoiceSeat
      ? needsVoiceSeatValue
      : blockedByOtherTab
        ? standbyValue
        : sessionPending
          ? CONNECTING_VALUE
          : INERT_VALUE;
    return (
      <DialstackSoftphoneContext.Provider value={value}>
        {children}
      </DialstackSoftphoneContext.Provider>
    );
  }

  return (
    <SoftphoneProvider
      token={token}
      appearance={softphoneAppearance}
      onTokenExpiring={refreshToken}
      onError={handleError}
      onConnectionStateChange={handleConnectionStateChange}
      onIncomingCall={handleIncomingCall}
      onCallStarted={handleCallStarted}
      onCallEnded={handleCallEnded}
    >
      <DialstackSoftphoneBridge
        userId={userId}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        popFolded={popFolded}
        setPopFolded={setPopFolded}
        connecting={sessionPending}
        scribeRecordingActive={scribeRecordingActive}
        lastCleanCallEndAtRef={lastCleanCallEndAtRef}
        deleteEmergencyAddress={deleteEmergencyAddress}
      >
        {children}
      </DialstackSoftphoneBridge>
    </SoftphoneProvider>
  );
}
